import React from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  showingFrom: number;
  showingTo: number;
  totalItems: number;
  itemsPerPage?: number;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  showingFrom,
  showingTo,
  totalItems,
  itemsPerPage = 10,
  onItemsPerPageChange
}) => {
  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else {
      if (totalPages > 1) {
        rangeWithDots.push(totalPages);
      }
    }

    return rangeWithDots;
  };

  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between px-2 py-4">
      <div className="flex items-center text-sm text-gray-700">
        <span>
          Mostrando <span className="font-medium">{showingFrom}</span> a{' '}
          <span className="font-medium">{showingTo}</span> de{' '}
          <span className="font-medium">{totalItems}</span> resultados
        </span>
        {onItemsPerPageChange && (
          <div className="ml-6 flex items-center">
            <label className="mr-2 text-sm font-medium text-gray-700">
              Mostrar:
            </label>
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-rose-500 focus:border-rose-500"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        )}
      </div>

      <div className="flex items-center space-x-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="sr-only">Página anterior</span>
        </Button>

        {getPageNumbers().map((pageNumber, index) => {
          if (pageNumber === '...') {
            return (
              <div
                key={`dots-${index}`}
                className="flex items-center justify-center w-8 h-8"
              >
                <MoreHorizontal className="w-4 h-4 text-gray-400" />
              </div>
            );
          }

          const isCurrentPage = pageNumber === currentPage;
          
          return (
            <Button
              key={pageNumber}
              variant={isCurrentPage ? "default" : "outline"}
              size="sm"
              onClick={() => onPageChange(pageNumber as number)}
              className={`w-8 h-8 ${
                isCurrentPage 
                  ? 'bg-rose-600 hover:bg-rose-700 text-white' 
                  : 'hover:bg-gray-50'
              }`}
            >
              {pageNumber}
            </Button>
          );
        })}

        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3"
        >
          <ChevronRight className="w-4 h-4" />
          <span className="sr-only">Página siguiente</span>
        </Button>
      </div>
    </div>
  );
};